/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ContentHeadingData } from 'common/models/content-heading';
import { AccountAdminProgramService } from 'private/app/services/account-admin/account-admin-program.service';
import { LanguageTranslationService } from 'common/services/language-translation.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { AccountAdminApiService } from 'private/app/services/account-admin/account-admin-api.service';
import { CountryOption, EligibilityCriteria, EligibleCompanies, ProgramSearchPayload, ProgramSearchResults, UpdateCriteriaData } from 'private/app/models/account-admin-program.model';
import { Option } from 'common/components/select/select.component';
import { EligibilityCriteriaQueryConfig } from './eligibilityCriteriaQueryConfig.service';
import { DynamicNestedFilterService } from 'src/common/services/dynamic-nested-filter.service';
import { AccountAdminEligibilityCriteriaService } from 'private/app/services/account-admin/account-admin-eligibility-criteria.service';
import { ToastService } from 'common/services/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { CommonUtilsService } from 'private/app/services/common-utils.service';
import { QueryBuilderConfig, Rule, RuleSet } from 'common/libs/angular2-query-builder/query-builder/query-builder.interfaces';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'hvac-edit-eligibility-criteria',
    templateUrl: './edit-eligibility-criteria.component.html',
    styleUrls: ['./edit-eligibility-criteria.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [AccountAdminEligibilityCriteriaService]
})
export class EditEligibilityCriteriaComponent implements OnInit, OnDestroy {
    ngOnDestroy$ = new Subject();
    public fieldCriteriaForm: UntypedFormGroup = new UntypedFormGroup({ criteriaName: new UntypedFormControl('') });
    public totalPages: number;
    public pageSize: number = 10;
    public currentPage: number = 1;
    public customerCompanyForm: UntypedFormGroup = new UntypedFormGroup({ customerCompanyId: new UntypedFormControl() });
    public distributorCompanyForm: UntypedFormGroup = new UntypedFormGroup({ distributorCompanyId: new UntypedFormControl() });
    public displayInputField: boolean;
    public criteriaNamesList$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public criteriaData: EligibilityCriteria;
    public eligibleCompanyData$: BehaviorSubject<EligibleCompanies[]> = new BehaviorSubject<EligibleCompanies[]>([]);
    public eligibleCompanyFilteredData$: BehaviorSubject<EligibleCompanies[]> = new BehaviorSubject<EligibleCompanies[]>([]);
    public isLoading: boolean;
    public queryBuilderConfigValues: QueryBuilderConfig = this.eligibilityCriteriaQueryConfigService.QueryBuilderConfigValues as QueryBuilderConfig;
    public breadCrumbData: ContentHeadingData = this.eligibilityCriteriaQueryConfigService.breadCrumbData;
    public queryDataToDisplay: RuleSet;
    public initialQuery: RuleSet = { ...this.eligibilityCriteriaQueryConfigService.emptyQueryData };
    public modifiedQueryDataToDisplay: RuleSet;
    public countryOptionData$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public selectedCountryId: string | null = 'US';
    public optionListForState: CountryOption[] = [];
    public bannerType = 'EditEligibiltyCriteria';
    public criteriaNameFromCreate: string;
    public criteriaIdFromCreate: string;
    public currentPageAfterFilter: number = 1;
    public totalPagesAfterFilter: number = 1;
    public criteriaDetails: UpdateCriteriaData = this.eligibilityCriteriaQueryConfigService.criteriaDetails;
    public disableFields: boolean = true;
    public isQueryUpdated = false;

    constructor(
        public translate: TranslateService,
        public accountAdminProgramService: AccountAdminProgramService,
        public queryStringService: DynamicNestedFilterService,
        public eligibilityCriteriaService: AccountAdminEligibilityCriteriaService,
        public eligibilityCriteriaQueryConfigService: EligibilityCriteriaQueryConfig,
        private toastService: ToastService,
        private route: ActivatedRoute,
        private router: Router,
        private languageTranslationService: LanguageTranslationService,
        private readonly accountAdminApiService: AccountAdminApiService,
        private commonUtils: CommonUtilsService
    ) {
    }

    ngOnInit(): void {
        this.route.queryParams
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((params) => {
                this.criteriaNameFromCreate = (params.criteriaName) ? params.criteriaName : '';
                this.criteriaIdFromCreate = (params.criteriaId) ? params.criteriaId : '';
                this.router.navigate([`/Admin/Program/search-program/edit-eligibility-criteria`], { replaceUrl: true });
            });
        this.languageTranslationService.translationsLoaded().subscribe(() => {
            this.accountAdminProgramService.setProgramAdminBreadCrumData(this.breadCrumbData);
        });
        this.displayInputField = false;
        this.getAccountAdminProgramEligibilityCriteria();
        this.getAllPrograms();
        this.commonUtils.getCountriesEntity().subscribe((countryList) => {
            this.queryBuilderConfigValues.fields.Country.options = countryList.map((country) => ({
                name: country.name,
                value: country.countryId.toString()
            }));
            this.optionListForState = countryList.map((country): CountryOption => ({
                code: country.code,
                value: country.countryId.toString()
            }));
        });
        this.fieldCriteriaForm.controls.criteriaName.setValue(this.criteriaNameFromCreate);
        this.getAccountAdminProgramCriteria();
    }

    ngOnDestroy() {
        this.eligibilityCriteriaService.ngOnDestroy();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    getStateList(selectedCountry: string) {
        return this.commonUtils.getStateByCountry(selectedCountry);
    }

    modifyQueryData($event: RuleSet) {
        $event.condition = $event.condition || 'and';
        const previousCountryId = this.selectedCountryId;
        this.modifiedQueryDataToDisplay = $event;
        this.selectedCountryId = JSON.stringify($event).split('"field":"Country","operator":"Equals","value":')[1]?.split('}')[0];
        this.selectedCountryId = (this.selectedCountryId) ? JSON.parse(this.selectedCountryId) : 'US';
        const countrySelected = this.isCountrySelected(this.modifiedQueryDataToDisplay);
        if (!countrySelected) {
            this.clearStateSelection($event);
            this.queryBuilderConfigValues = {
                ...this.queryBuilderConfigValues,
                fields: { ...this.queryBuilderConfigValues.fields }
            };
            delete this.queryBuilderConfigValues.fields['StateProvince'];

            return;
        }
        if (previousCountryId !== this.selectedCountryId && this.queryDataToDisplay && previousCountryId !== 'US') {
            this.clearStateSelection($event);
        }
        this.queryBuilderConfigValues = {
            ...this.queryBuilderConfigValues,
            fields: {
                ...this.queryBuilderConfigValues.fields,
                StateProvince: {
                    type: 'category',
                    name: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_OPTIONS.STATE'),
                    operators: [
                        this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_OPTIONS.EQUALS')
                    ],
                    options: []
                }
            }
        };
        const allSelectedCountries = this.getAllSelectedCountries($event);
        this.loadStatesForCountries(allSelectedCountries);
    }

    public getAllPrograms() {
        const criteriaDataForPages: ProgramSearchPayload = <ProgramSearchPayload>({
            criteria: {
                programName: '',
                programCode: '',
                programStatus: '',
                brandName: '',
                eligibleCriteriaName: ''
            },
            pageParams: {
                page: 0,
                pageSize: 0
            },
            sortFields: [],
            sortParams: [],
            determineTotalCount: true
        });
        this.accountAdminApiService.getAccountAdminProgramsBySearch((criteriaDataForPages)).subscribe((programResults: ProgramSearchResults) => {
            this.queryBuilderConfigValues.fields.ProgramId.options = programResults.data.map((program) => ({
                name: program.programName,
                value: program.programId.toString()
            }));
        });
    }

    getAccountAdminProgramCriteria() {
        this.currentPage = 1;
        this.queryDataToDisplay = { ...this.eligibilityCriteriaQueryConfigService.emptyQueryData };
        const criteriaId = (this.criteriaIdFromCreate) ? this.criteriaIdFromCreate : this.fieldCriteriaForm.controls.criteriaName.value[0]?.value;
        this.displayInputField = Boolean(criteriaId);
        this.criteriaDetails.criteriaId = criteriaId;
        this.criteriaDetails.criteriaName = (this.criteriaNameFromCreate) ? this.criteriaNameFromCreate : this.fieldCriteriaForm.controls.criteriaName.value[0]?.name;
        if (criteriaId) {
            this.disableFields = false;
            this.isLoading = true;
            this.accountAdminApiService.getAccountAdminProgramCriteria(criteriaId).subscribe((criteria: EligibilityCriteria) => {
                this.isLoading = false;
                this.criteriaData = criteria;
                if (criteria.filterStmt) {
                    const parsedQuery = this.queryStringService.parseExpression(criteria.filterStmt);
                    this.initialQuery = { ...parsedQuery };
                    this.initializeStateProvince(parsedQuery, () => {
                        this.queryDataToDisplay = parsedQuery;
                        this.modifiedQueryDataToDisplay = parsedQuery;
                    });
                }
                else {
                    this.queryDataToDisplay = { ...this.eligibilityCriteriaQueryConfigService.emptyQueryData };
                    this.modifiedQueryDataToDisplay = { ...this.eligibilityCriteriaQueryConfigService.emptyQueryData };
                }

                (criteria.dealerIds) ? this.eligibilityCriteriaService.getCustomerIdValue(criteria.dealerIds.split(',').map((line: string) => line.split(','))) : this.eligibilityCriteriaService.getCustomerIdValue([]);
                (criteria.distributorIds) ? this.eligibilityCriteriaService.getDistributorIdValue(criteria.distributorIds.split(',').map((line: string) => line.split(','))) : this.eligibilityCriteriaService.getDistributorIdValue([]);

                if (criteria.eligibleCompanies) {
                    this.totalPages = Math.ceil(criteria.eligibleCompanies.length / this.pageSize);
                    this.eligibleCompanyFilteredData$.next(criteria.eligibleCompanies);
                    this.eligibleCompanyData$.next(criteria.eligibleCompanies.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize));
                }
            },
            (err) => {
                this.isLoading = false;
                this.eligibilityCriteriaQueryConfigService.showToastBar(err);
            });
        }
    }

    onSubmit() {
        this.isLoading = true;
        this.criteriaDetails.programIds = [];
        this.criteriaDetails.selectedFields = this.queryStringService.getSelectedFields(this.modifiedQueryDataToDisplay);
        this.criteriaDetails.criteriaFilter = this.queryStringService.basicRulesetToQueryString(this.modifiedQueryDataToDisplay);
        this.criteriaDetails.dealerCompanyIds = this.eligibilityCriteriaService.customerCompanyfilteredData$.value.flat(1).map(Number);
        this.criteriaDetails.distributorCompanyIds = this.eligibilityCriteriaService.distributorCompanyfilteredData$.value.flat(1).map(Number);
        if (JSON.stringify(this.modifiedQueryDataToDisplay).includes('ProgramId')) {
            const programId = JSON.stringify(this.modifiedQueryDataToDisplay).split('"field":"ProgramId","operator":"Equals","value":')[1]?.split('}')[0];
            this.criteriaDetails.programIds.push(Number(JSON.parse(programId)));
        }
        this.accountAdminApiService.postUpdateEligibilityCriteria(this.criteriaDetails).subscribe((response) => {
            this.accountAdminProgramService.smoothScrollToTop();
            if (response.id) {
                this.isLoading = false;
                this.toastService.add({
                    content: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_UPDATE_SUCCESS', { criteriaName: response.name }),
                    theme: 'success',
                    id: this.bannerType,
                    closeable: true,
                    autoClose: true
                });
                this.getAccountAdminProgramCriteria();
            }
        },
        (err) => {
            this.isLoading = false;
            this.eligibilityCriteriaQueryConfigService.showToastBar(err);
        });
    }

    handlePageChange(page: number) {
        this.currentPage = parseInt(page.toString(), this.pageSize);
        this.eligibleCompanyData$.next(
            this.eligibleCompanyFilteredData$.value.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize)
        );
    }

    onCancel() {
        this.fieldCriteriaForm.controls.criteriaName.setValue('');
        this.disableFields = true;
        this.criteriaData.eligibleCompanies = [];
        this.queryDataToDisplay = { ...this.eligibilityCriteriaQueryConfigService.emptyQueryData };
        this.modifiedQueryDataToDisplay = { ...this.eligibilityCriteriaQueryConfigService.emptyQueryData };
        this.eligibilityCriteriaService.getCustomerIdValue([]);
        this.eligibilityCriteriaService.getDistributorIdValue([]);
        this.eligibleCompanyData$.next([]);
        this.totalPages = 0;
        this.accountAdminProgramService.smoothScrollToTop();
        this.eligibilityCriteriaService.isCustomerTableVisible = false;
        this.eligibilityCriteriaService.isDistributorTableVisible = false;
    }

    hasCompanyIds(): boolean {
        return (this.eligibilityCriteriaService.customerCompanyfilteredData$.value?.length || 0) > 0
            || (this.eligibilityCriteriaService.distributorCompanyfilteredData$.value?.length || 0) > 0;
    }

    isRulesetObject(obj: unknown): obj is RuleSet {
        return Array.isArray((obj as RuleSet)?.rules);
    }

    hasEmptyRuleset(ruleset: RuleSet): boolean {
        if (!ruleset || ruleset?.rules?.length === 0) {
            return true;
        }

        let isAnyRulesetEmpty = false;

        for (let i = 0; i < (ruleset?.rules?.length || 0); i++) {
            if (isAnyRulesetEmpty) {
                break;
            }

            const rule = ruleset?.rules[i];

            if (this.isRulesetObject(rule)) {
                isAnyRulesetEmpty = this.hasEmptyRuleset(rule);
            }
            else {
                isAnyRulesetEmpty = this.isRuleValueEmpty(rule);
            }
        }

        return isAnyRulesetEmpty;
    }

    isRuleValueEmpty(rule: Rule): boolean {
        const { value } = rule;
        if (value === null || typeof value === 'undefined') {
            return true;
        }

        return typeof value === 'string' && value.trim().length === 0;
    }

    private getAccountAdminProgramEligibilityCriteria() {
        this.accountAdminApiService.getAccountAdminProgramEligibilityCriteria().subscribe((criteriaNames: EligibilityCriteria[]) => {
            const optionList: Option[] = [];
            criteriaNames.map((criteria: EligibilityCriteria) => optionList.push({
                name: criteria.name,
                value: criteria.id
            }));
            const sortedOptions = optionList?.slice().sort((valueA, valueB) => valueA.name.toLowerCase().localeCompare(valueB.name.toLowerCase()));
            sortedOptions.unshift({
                name: this.translate.instant('ACCOUNT_ADMIN.SELECT_CRITERIA'),
                value: ''
            });
            this.criteriaNamesList$.next(sortedOptions);
        });
    }

    private clearStateSelection(ruleSet: RuleSet) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const clearStateFromRules = (rules: any[]): any[] => rules.filter((rule) => rule.field !== 'StateProvince').map((rule) => (
            Array.isArray(rule.rules)
                ? {
                    ...rule,
                    rules: clearStateFromRules(rule.rules)
                }
                : rule
        ));
        ruleSet.rules = clearStateFromRules(ruleSet.rules);
        this.modifiedQueryDataToDisplay = { ...ruleSet };
    }

    private isCountrySelected(ruleSet: RuleSet): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return ruleSet.rules.some((rule: any) => {
            if (rule.field === 'Country' && rule.value) {
                return true;
            }
            if (rule.rules) {
                return this.isCountrySelected(rule);
            }

            return false;
        });
    }

    private initializeStateProvince(ruleSet: RuleSet, callback?: () => void) {
        const countrySelected = this.isCountrySelected(ruleSet);
        const stateSelected = this.isStateSelected(ruleSet);
        if (!countrySelected && !stateSelected) {
            if (callback) {
                callback();
            }

            return;
        }
        if (!this.queryBuilderConfigValues.fields['StateProvince']) {
            this.queryBuilderConfigValues = {
                ...this.queryBuilderConfigValues,
                fields: {
                    ...this.queryBuilderConfigValues.fields,
                    StateProvince: {
                        type: 'category',
                        name: this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_OPTIONS.STATE'),
                        operators: [
                            this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.PROGRAM_ELIGIBILITY_CRITERIA.CRITERIA_OPTIONS.EQUALS')
                        ],
                        options: []
                    }
                }
            };
        }
        const countryValue = this.getCountryValue(ruleSet);
        const selectedCountryCode = this.optionListForState.find((entry) => String(entry.value) === countryValue)?.code;
        if (selectedCountryCode) {
            this.getStateList(selectedCountryCode).subscribe({
                next: (stateList) => {
                    this.queryBuilderConfigValues = {
                        ...this.queryBuilderConfigValues,
                        fields: {
                            ...this.queryBuilderConfigValues.fields,
                            StateProvince: {
                                ...this.queryBuilderConfigValues.fields.StateProvince,
                                options: stateList.map((state) => ({
                                    name: state.name,
                                    value: state.value
                                }))
                            }
                        }
                    };
                    if (callback) {
                        callback();
                    }
                },
                error: (err: HttpErrorResponse) => {
                    if (err?.status === 404) {
                        this.queryBuilderConfigValues = {
                            ...this.queryBuilderConfigValues,
                            fields: {
                                ...this.queryBuilderConfigValues.fields,
                                StateProvince: {
                                    ...this.queryBuilderConfigValues.fields.StateProvince,
                                    options: []
                                }
                            }
                        };
                    }
                    else {
                        this.eligibilityCriteriaQueryConfigService.showToastBar(err);
                    }
                    if (callback) {
                        callback();
                    }
                }
            });
        }
        else if (callback) {
            callback();
        }
    }

    private isStateSelected(ruleSet: RuleSet): boolean {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return ruleSet.rules.some((rule: any) => {
            if (rule.field === 'StateProvince' && rule.value) {
                return true;
            }
            if (rule.rules) {
                return this.isStateSelected(rule);
            }

            return false;
        });
    }

    private getAllSelectedCountries(ruleSet: RuleSet): string[] {
        const countries: string[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const extractCountries = (rules: any[]) => {
            rules.forEach((rule) => {
                if (rule.field === 'Country' && rule.value) {
                    const countryCode = this.optionListForState.find((entry) => String(entry.value) === rule.value)?.code;
                    if (countryCode && !countries.includes(countryCode)) {
                        countries.push(countryCode);
                    }
                }
                if (rule.rules) {
                    extractCountries(rule.rules);
                }
            });
        };
        extractCountries(ruleSet.rules);

        return countries;
    }

    private loadStatesForCountries(countryCodes: string[]) {
        if (countryCodes.length === 0) {
            return;
        }
        const statePromises = countryCodes.map((code) => this.commonUtils.getStateEntityByCountry(code).toPromise().catch(() => []));
        Promise.all(statePromises).then((stateArrays) => {
            const allStates = stateArrays.flat().map((state) => ({
                name: state.name,
                value: state.stateId
            }));
            this.queryBuilderConfigValues = {
                ...this.queryBuilderConfigValues,
                fields: {
                    ...this.queryBuilderConfigValues.fields,
                    StateProvince: {
                        ...this.queryBuilderConfigValues.fields.StateProvince,
                        options: allStates
                    }
                }
            };
        });
    }

    private getCountryValue(ruleSet: RuleSet): string | null {
        for (const rule of ruleSet.rules) {
            if (rule.field === 'Country' && rule.value) {
                return rule.value;
            }
            if (rule.rules) {
                const countryValue = this.getCountryValue(rule);
                if (countryValue) {
                    return countryValue;
                }
            }
        }

        return null;
    }
}
